/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CellComponent, ColumnDefinition, RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';
import { t, tx, useLang } from '../../i18n';
import { TableGrid } from './TableGrid';
import { recordUndo } from './undo';

/** Описание столбца таблицы ввода. */
export interface InputGridColumn {
  key: string;
  title: string;
  width: number;
  /** Столбец заполняется программой (серый, не редактируется). */
  ref?: boolean;
  required?: boolean;
  align?: 'left' | 'right';
  /** Значение справочного столбца по строке. */
  value?: (row: Record<string, string>) => string;
  group?: 'input' | 'ref' | 'qty' | 'money';
}

export type RowCheckResult = { ok: true } | { ok: false; message: string } | null;

export interface InputGridHandle {
  /** Строки с данными (пустые отброшены). */
  rows: () => Record<string, string>[];
  /** Заменить содержимое таблицы. */
  setRows: (rows: Record<string, string>[]) => void;
  /** Пересчитать подсказки и проверки. */
  refresh: () => void;
  /** Номера строк с ошибками — как слева в таблице. */
  errorRows: () => number[];
  /** Прокрутить к первой строке с ошибкой и подсветить её. */
  showFirstError: () => void;
}

interface Props {
  gridId: string;
  columns: InputGridColumn[];
  /**
   * Проверка всей таблицы сразу: результат для каждой строки по порядку (null — строка пустая).
   * Проверяется таблица целиком, потому что часть ошибок видна только между строками (повтор OEM).
   */
  check: (rows: Record<string, string>[]) => RowCheckResult[];
  onChange: () => void;
  handle: { current: InputGridHandle | null };
  /** Сколько пустых строк держать в конце. */
  spare?: number;
  height?: string;
}

const SPARE = 8;

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

function blank(columns: InputGridColumn[], count: number): Record<string, string>[] {
  return Array.from({ length: count }, () => Object.fromEntries(columns.map((c) => [c.key, ''])) as Record<string, string>);
}

const CHECK_ICON =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

/**
 * Таблица ввода на Tabulator: одно нажатие выделяет ячейку, двойное — открывает правку.
 * Вставка блоком из Excel, выделение диапазонов,
 * справочные столбцы и столбец «Проверка» пересчитываются на лету — по всей таблице сразу.
 */
export function InputGrid({ gridId, columns, check, onChange, handle, spare = SPARE, height }: Props) {
  const table = useRef<Tabulator | null>(null);
  const lang = useLang();
  // Проверка меняется вместе с шапкой формы — таблица берёт всегда свежую
  const checkRef = useRef(check);
  checkRef.current = check;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Результат проверки по строке: ключ — объект данных строки (Tabulator хранит его, пока строка жива)
  const results = useRef(new WeakMap<object, RowCheckResult>());
  const order = useRef<RowCheckResult[]>([]);

  /** Проверить всю таблицу и перерисовать строки. */
  const recheck = useCallback((instance: Tabulator | null) => {
    if (!instance) return;
    const rows = instance.getRows('active');
    const data = rows.map((r) => r.getData() as Record<string, string>);
    const res = checkRef.current(data);
    const map = new WeakMap<object, RowCheckResult>();
    data.forEach((d, i) => map.set(d, res[i] ?? null));
    results.current = map;
    order.current = res;
    rows.forEach((r) => r.reformat());
  }, []);

  const ensureSpare = useCallback(
    (instance: Tabulator) => {
      const data = instance.getData() as Record<string, string>[];
      let empty = 0;
      for (let i = data.length - 1; i >= 0 && Object.values(data[i]!).every((v) => !v); i--) empty++;
      if (empty < spare) void instance.addData(blank(columns, spare - empty));
    },
    [columns, spare]
  );

  // Изменилась шапка формы (№ запроса, режим) — проверка другая, пересчитать
  useEffect(() => recheck(table.current), [check, recheck]);

  const resultOf = (row: RowComponent): RowCheckResult => results.current.get(row.getData() as object) ?? null;

  const tabColumns = useMemo<ColumnDefinition[]>(() => {
    const cols: ColumnDefinition[] = columns.map((col) => ({
      title: col.title,
      field: col.key,
      width: col.width,
      editor: col.ref ? undefined : ('input' as const),
      cssClass: col.ref ? 'cell-ref' : col.required ? 'cell-input cell-required' : 'cell-input',
      formatter: col.ref
        ? (cell: CellComponent) => col.value?.(cell.getRow().getData() as Record<string, string>) ?? ''
        : undefined
    }));
    cols.push({
      title: t('Проверка'),
      field: '__check',
      width: 230,
      editor: undefined,
      headerSort: false,
      formatter: (cell: CellComponent) => {
        const res = resultOf(cell.getRow());
        const el = cell.getElement();
        el.classList.remove('tabulator-cell-ok', 'tabulator-cell-error');
        el.title = '';
        if (!res) return '';
        el.classList.add(res.ok ? 'tabulator-cell-ok' : 'tabulator-cell-error');
        if (!res.ok) el.title = tx(res.message);
        return res.ok
          ? `<span class="t-check">${CHECK_ICON}${escapeHtml(t('Готово'))}</span>`
          : `<span class="t-error">${escapeHtml(tx(res.message))}</span>`;
      }
    });
    return cols;
    // lang — чтобы заголовки и проверки перерисовались на новом языке
  }, [columns, lang]);

  // Пустые строки создаются один раз: иначе перерисовка React стирала бы введённые данные
  const initial = useMemo(() => blank(columns, spare + 4), []);

  return (
    <TableGrid
      gridId={gridId}
      columns={tabColumns}
      data={initial}
      height={height}
      variant="sheet"
      blankRow={() => blank(columns, 1)[0]!}
      onChanged={() => {
        const instance = table.current;
        if (!instance) return;
        ensureSpare(instance);
        recheck(instance);
        onChangeRef.current();
      }}
      options={{
        layout: 'fitColumns',
        rowFormatter: (row: RowComponent) => {
          const res = resultOf(row);
          row.getElement().classList.toggle('row-error', !!res && !res.ok);
        }
      }}
      onReady={(instance) => {
        table.current = instance;
        handle.current = {
          rows: () => (instance.getData() as Record<string, string>[]).filter((r) => Object.values(r).some((v) => v)),
          setRows: (rows) => {
            // Очистку и загрузку строк тоже можно отменить Ctrl+Z
            recordUndo(instance);
            void instance.replaceData([...rows, ...blank(columns, spare)] as never[]).then(() => recheck(instance));
            onChange();
          },
          refresh: () => {
            recheck(instance);
            instance.redraw(true);
          },
          errorRows: () => order.current.map((r, i) => (r && !r.ok ? i + 1 : 0)).filter((n) => n > 0),
          showFirstError: () => {
            const rows = instance.getRows('active');
            const first = rows.find((r) => {
              const res = resultOf(r);
              return !!res && !res.ok;
            });
            if (!first) return;
            void instance.scrollToRow(first, 'center', false).then(() => {
              const el = first.getElement();
              el.classList.remove('row-flash');
              void el.offsetWidth;
              el.classList.add('row-flash');
              setTimeout(() => el.classList.remove('row-flash'), 1600);
            });
          }
        };
        instance.on('cellEdited', () => {
          ensureSpare(instance);
          recheck(instance);
          onChangeRef.current();
        });
        instance.on('dataChanged', () => {
          recheck(instance);
          instance.redraw();
          onChangeRef.current();
        });
        recheck(instance);
        onChangeRef.current();
      }}
    />
  );
}
